// src/app/components/tabs/WhatsAppFormattedText.tsx
//
// Texto de mensagem do WhatsApp com a mesma formatação que o app mostra:
// *negrito*, _itálico_, ~tachado~, `código` e ```bloco```, além de links
// clicáveis. Monta nós React (nunca HTML cru) e só aceita links http(s).

import { Fragment, useMemo, type ReactNode } from 'react';

const CODE_BLOCK_RE = /(```[\s\S]+?```)/g;
const URL_RE = /((?<![\w@])(?:https?:\/\/|www\.)[^\s<]+[^\s<.,:;"')\]!?*_~])/gi;
// Marcador só vale colado ao texto e com borda de palavra dos dois lados —
// mesma regra do WhatsApp ("2*3*4" não vira negrito).
const STYLE_RE = /(^|[\s([{"'.,!?;:])([*_~`])(?![\s*_~`])([^\n]*?[^\s])\2(?=$|[\s)\]}"'.,!?;:])/;

/** Minúsculas e sem acento — "Matrícula" casa com "matricula". */
export function normalizeForSearch(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * Divide `text` nos trechos que casam com `term` (sem diferenciar acento nem
 * maiúscula), preservando o texto original — para destacar o resultado da
 * busca sem alterar o que foi escrito.
 */
export function splitHighlight(text: string, term: string): { text: string; hit: boolean }[] {
  const needle = normalizeForSearch(term.trim());
  if (!needle) return [{ text, hit: false }];
  // Cada caractere normalizado aponta para o índice do caractere original.
  let normalized = '';
  const origIndex: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const n = normalizeForSearch(text[i]);
    for (let j = 0; j < n.length; j += 1) {
      normalized += n[j];
      origIndex.push(i);
    }
  }
  const parts: { text: string; hit: boolean }[] = [];
  let cursor = 0;
  let from = normalized.indexOf(needle);
  while (from !== -1) {
    const start = origIndex[from];
    const end = origIndex[from + needle.length - 1] + 1;
    if (start > cursor) parts.push({ text: text.slice(cursor, start), hit: false });
    parts.push({ text: text.slice(start, end), hit: true });
    cursor = end;
    from = normalized.indexOf(needle, from + needle.length);
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), hit: false });
  return parts;
}

function withHighlight(text: string, term: string | undefined, key: string): ReactNode {
  if (!term) return text;
  const parts = splitHighlight(text, term);
  if (parts.length === 1 && !parts[0].hit) return text;
  return parts.map((p, i) =>
    p.hit ? (
      <mark key={`${key}-h${i}`} className="bg-amber-200 text-inherit rounded-sm px-0.5" data-testid="search-hit">
        {p.text}
      </mark>
    ) : (
      <Fragment key={`${key}-h${i}`}>{p.text}</Fragment>
    ),
  );
}

function wrap(marker: string, children: ReactNode, key: string): ReactNode {
  switch (marker) {
    case '*':
      return <strong key={key} className="font-semibold">{children}</strong>;
    case '_':
      return <em key={key}>{children}</em>;
    case '~':
      return <s key={key}>{children}</s>;
    default:
      return (
        <code key={key} className="font-mono text-[0.85em] bg-black/5 px-1 rounded">
          {children}
        </code>
      );
  }
}

function renderStyled(text: string, key: string, highlight?: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let i = 0;
  while (rest) {
    const m = STYLE_RE.exec(rest);
    if (!m) {
      out.push(withHighlight(rest, highlight, `${key}-t${i}`));
      break;
    }
    const start = m.index + m[1].length;
    if (start > 0) out.push(withHighlight(rest.slice(0, start), highlight, `${key}-p${i}`));
    const marker = m[2];
    const inner = m[3];
    const k = `${key}-${i++}`;
    out.push(wrap(marker, marker === '`' ? withHighlight(inner, highlight, k) : renderStyled(inner, k, highlight), k));
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

export function formatWhatsAppText(text: string, highlight?: string): ReactNode[] {
  const out: ReactNode[] = [];
  let k = 0;
  for (const part of text.split(CODE_BLOCK_RE)) {
    if (!part) continue;
    if (part.length > 6 && part.startsWith('```') && part.endsWith('```')) {
      out.push(
        <code key={k++} className="block my-1 font-mono text-[0.85em] whitespace-pre-wrap bg-black/5 px-2 py-1 rounded">
          {part.slice(3, -3)}
        </code>,
      );
      continue;
    }
    part.split(URL_RE).forEach((seg, idx) => {
      if (!seg) return;
      if (idx % 2 === 1) {
        const href = /^https?:\/\//i.test(seg) ? seg : `https://${seg}`;
        out.push(
          <a
            key={k++}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-sky-700 underline underline-offset-2 break-all hover:text-sky-800"
          >
            {withHighlight(seg, highlight, `l${k}`)}
          </a>,
        );
      } else {
        out.push(<Fragment key={k++}>{renderStyled(seg, `s${k}`, highlight)}</Fragment>);
      }
    });
  }
  return out;
}

export default function WhatsAppFormattedText({ text, highlight }: { text: string; highlight?: string }) {
  const nodes = useMemo(() => formatWhatsAppText(text, highlight), [text, highlight]);
  return <span className="whitespace-pre-wrap break-words">{nodes}</span>;
}
